/**
 * Resolves a Candid document tab's storage path and previews the file.
 *
 * The tab carries a bucket-relative GCS path (e.g.
 * `/medical_pa_order/<order_id>/j_code_docs/<uuid>.pdf`), so the download
 * URL is resolved first and PDF vs image is chosen from the extension.
 *
 * @module candidRpaDocumentPreviewPanel
 */
import { CandidDocumentPreviewTab } from "../../../pages/worklists/singleOrder/candidUpload/types/candidDocumentPreviewTab";
import CandidRpaAuthLetterPdfPanel from "./candidRpaAuthLetterPdfPanel";
import CandidRpaDocumentPreviewStatus from "./candidRpaDocumentPreviewStatus";
import CandidRpaImageViewerPanel from "./candidRpaImageViewerPanel";
import { isImageDocumentPath } from "./getDocumentPreviewKind";
import { useCandidDocumentDownloadUrl } from "./hooks/useCandidDocumentDownloadUrl";

/** Props for {@link CandidRpaDocumentPreviewPanel}. */
export interface CandidRpaDocumentPreviewPanelProps {
  /** Active sidenav tab, whose `src` is the GCS object path. */
  tab: CandidDocumentPreviewTab;
}

/**
 * Renders the active document tab, showing loading and error states while
 * the storage path is turned into a download URL.
 *
 * @param props.tab - Active document preview tab
 * @returns Preview, loading, or error panel for the tab
 */
const CandidRpaDocumentPreviewPanel = ({
  tab,
}: CandidRpaDocumentPreviewPanelProps) => {
  const { url, isLoading, error } = useCandidDocumentDownloadUrl(tab.src);

  if (isLoading) {
    return (
      <CandidRpaDocumentPreviewStatus
        name={tab.name}
        time={tab.time}
        message="Loading document..."
        isLoading
      />
    );
  }

  if (error || !url) {
    return (
      <CandidRpaDocumentPreviewStatus
        name={tab.name}
        time={tab.time}
        message={error || "No document available"}
        isError={Boolean(error)}
      />
    );
  }

  if (isImageDocumentPath(tab.src)) {
    return (
      <CandidRpaImageViewerPanel url={url} name={tab.name} time={tab.time} />
    );
  }

  return (
    <CandidRpaAuthLetterPdfPanel src={url} name={tab.name} time={tab.time} />
  );
};

export default CandidRpaDocumentPreviewPanel;
